import React from 'react'
// libs
import { FieldArray } from 'redux-form'
import { useQuery } from '@apollo/client/react'
// components
import MappedProperties from './MappedProperties'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// graphql
import { RISK_TEMPLATE_QUERY } from 'graphql/risk/queries'
import { INJURY_MASTER_INCIDENT_PROPERTIES_QUERY } from 'graphql/injuryMaster/queries'
// types
import { IField, IInjuryMasterIncidentProperty, IRiskTemplate } from 'types/graphql'
// views
import { Description, Title } from './views'
// utils
import { getSourceFields } from './utils'

interface Props {
  // The transaction's form path, e.g. transactions[0]
  field: string
  description: string
  // Only creating a case needs Injury Master's required properties
  isCreate?: boolean
  // A parent case transaction runs on a related form and may also read its parent form's fields
  withParentFields?: boolean
}

// The list of Skefto field -> Injury Master property mappings, shared by Create Case and Update Case.
const InjuryMasterMapping: React.FC<Props> = ({ field, description, isCreate, withParentFields }) => {
  const { riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const { data } = useQuery<{ injuryMasterIncidentProperties: IInjuryMasterIncidentProperty[] }>(
    INJURY_MASTER_INCIDENT_PROPERTIES_QUERY
  )

  // The templates this form is a related form of
  const parentTemplatesIds = riskTemplate?.relatedRiskTemplatesIdsFromSystemProcesses ?? []

  const { data: parentTemplatesData } = useQuery<{ riskTemplates: { data: IRiskTemplate[] } }>(RISK_TEMPLATE_QUERY, {
    skip: !withParentFields || !parentTemplatesIds.length,
    variables: { filter: { ids: parentTemplatesIds } }
  })

  const incidentProperties = data?.injuryMasterIncidentProperties ?? []
  const parentTemplates = parentTemplatesData?.riskTemplates?.data ?? []

  // An update patches only what it maps, so nothing is added up front, locked or starred.
  const properties = React.useMemo(
    () =>
      isCreate ? incidentProperties : incidentProperties.map((property) => ({ ...property, requiredOnCreate: false })),
    [incidentProperties, isCreate]
  )
  const templateFields: IField[] = React.useMemo(
    () => getSourceFields(riskTemplate?.fields ?? [], parentTemplates),
    [riskTemplate?.fields, parentTemplates]
  )

  return (
    <div>
      <Title>Field mapping</Title>
      <Description>{description}</Description>
      <FieldArray<any>
        name={`${field}.propertyMappings`}
        component={MappedProperties as any}
        properties={properties}
        templateFields={templateFields}
        parentTemplates={parentTemplates}
      />
    </div>
  )
}

export default InjuryMasterMapping
